import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import clsx from 'clsx'
import { memo, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'shared/hooks'

import type { Message, Participant, RenderContent } from '../../model/types'

import attach from '../../../../assets/image/icons/svg/attach.svg'
import star from '../../../../assets/image/icons/svg/star.svg'
import user from '../../../../assets/image/icons/svg/user.svg'
import { appSelectors } from '../../../../redux/reducers/app'
import { api } from '../../api'
import { selectors } from '../../utils/selectors'
import {
	findRenderContent,
	formatDateTime,
	getConversationUrl
} from '../../utils/utils'
import { openActions, useOpened } from '../QuickActions'
import css from './Thumbnail.module.scss'

const contentTypePreviewText: Partial<Record<keyof RenderContent, string>> = {
	audio: 'a voice message',
	external_media: 'a GIF',
	file: 'an attachment',
	unsupported_content: 'a post',
	vector_image: 'an attachment',
	video_meeting: 'a video meeting'
}

function PreviewText({
	conversationalist,
	conversationType,
	isInterlocutor,
	message,
	renderContent
}: {
	conversationalist?: Participant
	conversationType: null | string

	isInterlocutor: boolean
	message?: Message
	renderContent: keyof RenderContent | null
}) {
	const sender = isInterlocutor
		? `${conversationalist?.firstName} sent`
		: 'You sent'

	const subject = message?.subject

	if (renderContent) {
		const showAttachIcon =
			(!subject && renderContent === 'vector_image') || renderContent === 'file'

		let text = subject || contentTypePreviewText[renderContent]

		if (!conversationType) {
			text = `${sender} ${text}`
		}

		if (showAttachIcon) {
			return (
				<p className={css.message}>
					{conversationType && (
						<span className={css.type}>{conversationType}</span>
					)}
					<img alt='attach' height={14} src={attach} width={14} />
					<span>{text}</span>
				</p>
			)
		}

		return (
			<>
				{conversationType && (
					<span className={css.type}>{conversationType}</span>
				)}
				{` ${text}`}
			</>
		)
	}

	return subject || message?.text || 'No message'
}

function Thumbnail_({
	id,
	isSelected = false
}: { id: string; isSelected?: boolean }) {
	const conversation = useAppSelector(state =>
		selectors.conversations.selectById(state, id)
	)

	const {
		conversation_type: conversationType,

		events,
		headline,
		is_starred: isStarred,

		participants,

		unread_count: unread
	} = conversation

	const dispatch = useAppDispatch()
	const [readMutation] = api.useMarkReadMutation()

	const userEntityId = useAppSelector(appSelectors.personIdLi)

	const message: Message | undefined = events[0]

	const { openClass, triggerRef } = useOpened(id)

	const isMessageDeleted = message?.render_format === 'RECALLED'
	const isSystemMessage = message?.render_format === 'SYSTEM'

	const conversationalist = useMemo(
		() =>
			participants.find(participant => participant.entityId !== userEntityId),
		[participants, userEntityId]
	)

	const isInterlocutor = conversationalist?.entityId === message?.from?.entityId
	const renderContent = findRenderContent(message?.render_content)

	const handleMailboxItemClick = () => {
		dispatch(api.util.upsertQueryData('participants', id, participants))
		if (unread) {
			readMutation({ id, mark: true })
		}
	}
	const showUnread = Boolean(unread)

	return (
		<li className={css.root}>
			<img
				alt='avatar'
				className={css.avatar}
				src={conversationalist?.pictureUrl || user}
			/>

			<Link
				aria-current={isSelected && 'page'}
				className={css.title}
				onClick={handleMailboxItemClick}
				to={getConversationUrl(id)}
			>
				{conversationalist?.fullName}
			</Link>

			<button
				aria-haspopup='true'
				aria-label='Open menu'
				className={clsx(css.actionTrigger, openClass)}
				onClick={openActions}
				ref={triggerRef}
				type='button'
				value={id}
			>
				<MoreHorizIcon />
			</button>
			<time className={css.time} dateTime={message.created_at}>
				{formatDateTime(message.created_at)}
			</time>

			{headline && <p className={css.text}>{headline}</p>}
			<div className={css.body}>
				{isMessageDeleted ? (
					<p className='mailbox-user-text'>This message has been deleted.</p>
				) : (
					<div className='mailbox-user-text'>
						{!renderContent && !isSystemMessage && (
							<span
								className={clsx(
									conversationType && 'mailbox-conversation-type'
								)}
							>
								{conversationType
									? `${conversationType} `
									: isInterlocutor
										? `${conversationalist?.firstName}: `
										: 'You: '}
							</span>
						)}
						<PreviewText
							conversationalist={conversationalist}
							conversationType={conversationType}
							isInterlocutor={isInterlocutor}
							message={message}
							renderContent={renderContent}
						/>
					</div>
				)}
				{isStarred && <img alt='star' className='mailbox-star' src={star} />}
				{showUnread && <div className='mailbox-unread'>{unread}</div>}
			</div>
		</li>
	)
}

export const Thumbnail = memo(Thumbnail_)
